'use client';

import { type JSX, useCallback, useRef, useState } from 'react';
import { DraggableCore, type DraggableEventHandler } from 'react-draggable';
import { twMerge } from 'tailwind-merge';

import { Tooltip } from '@videoblocks/design-system';

import { DurationLabel } from './DurationLabel';
import { TrimHandle } from './TrimHandle';
import { type VideoSelection } from './types';
import { useMoveDrag } from './useMoveDrag';
import { useSelectionKeyboard } from './useSelectionKeyboard';
import { useTrimDrag } from './useTrimDrag';
import { constrainSelectionTime, timeToAbsolutePixel } from './videoUtils';

interface SelectionIndicatorProps {
    selection: VideoSelection;
    videoDuration: number;
    stripWidth: number;
    stripHeight: number;
    onSelectionMove: (startTime: number) => void;
    onSelectionChange: (selection: VideoSelection) => void;
    className?: string;
    onSeek?: (time: number) => void;
    onPlayPause?: () => void;
    isPlaying?: boolean;
}

function EndCap({ side }: { side: 'left' | 'right' }): JSX.Element {
    const barBase = 'absolute top-1/2 -translate-y-1/2 w-[2px] rounded-capsule bg-neutral-dark-200';
    const isLeft = side === 'left';

    return (
        <div
            className={twMerge(
                'absolute inset-y-0 w-[11px] bg-yellow-400 pointer-events-none',
                isLeft ? 'left-0 rounded-tl-sm rounded-bl-sm' : 'right-0 rounded-tr-sm rounded-br-md',
            )}
        >
            {/* 2px pad, 2px short bar, 3px gap, 2px tall bar, 2px pad = the 11px cap. */}
            <span className={twMerge(barBase, 'h-[20px]', isLeft ? 'left-[2px]' : 'right-[2px]')} />
            <span className={twMerge(barBase, 'h-[24px]', isLeft ? 'left-[7px]' : 'right-[7px]')} />
        </div>
    );
}

export function SelectionIndicator({
    selection,
    videoDuration,
    stripWidth,
    stripHeight,
    onSelectionMove,
    onSelectionChange,
    className = '',
    onSeek,
    onPlayPause,
    isPlaying = false,
}: SelectionIndicatorProps): JSX.Element {
    const containerRef = useRef<HTMLDivElement>(null);
    const selectionRef = useRef<HTMLDivElement>(null);
    const lastMoveX = useRef<number>(0);
    const [isMoving, setIsMoving] = useState(false);

    const selectionWidth = timeToAbsolutePixel(selection.duration, videoDuration, stripWidth);

    const constrainedOnSelectionMove = useCallback(
        (startTime: number): void => {
            onSelectionMove?.(constrainSelectionTime(startTime, selection.duration, videoDuration));
        },
        [onSelectionMove, videoDuration, selection.duration],
    );

    const { handleKeyDown } = useSelectionKeyboard(
        videoDuration,
        constrainedOnSelectionMove,
        onSeek,
        onPlayPause,
        containerRef,
    );

    const { handleMoveStart, handleMoveDrag, handleMoveStop } = useMoveDrag({
        selection,
        videoDuration,
        stripWidth,
        onSelectionMove: constrainedOnSelectionMove,
        onSeek,
        isPlaying,
        onPlayPause,
    });

    const {
        isDragging,
        leftTooltipVisible,
        rightTooltipVisible,
        handleLeftTrimStart,
        handleLeftTrimDrag,
        handleLeftTrimStop,
        handleRightTrimStart,
        handleRightTrimDrag,
        handleRightTrimStop,
        nudgeLeftTrim,
        nudgeRightTrim,
    } = useTrimDrag({
        selection,
        videoDuration,
        stripWidth,
        onSelectionChange,
        onSeek,
        isPlaying,
        onPlayPause,
    });

    const handleDragStart: DraggableEventHandler = useCallback(
        (_e, data) => {
            lastMoveX.current = data.x;
            setIsMoving(true);
            handleMoveStart();
        },
        [handleMoveStart],
    );

    const handleDrag: DraggableEventHandler = useCallback(
        (_e, data) => {
            // DO NOT update lastMoveX here — useMoveDrag needs cumulative delta from drag start
            handleMoveDrag(data.x - lastMoveX.current);
        },
        [handleMoveDrag],
    );

    const handleDragStop: DraggableEventHandler = useCallback(() => {
        lastMoveX.current = 0;
        setIsMoving(false);
        handleMoveStop();
    }, [handleMoveStop]);

    // Track the cursor exactly while dragging; snap to whole pixels at rest so
    // both end caps rasterize identically instead of antialiasing unevenly.
    const snap = (value: number): number => (isMoving || isDragging ? value : Math.round(value));

    const constrainedStartTime = constrainSelectionTime(selection.startTime, selection.duration, videoDuration);
    const selectionLeft = timeToAbsolutePixel(constrainedStartTime, videoDuration, stripWidth);

    return (
        <div className={twMerge('absolute pointer-events-none top-0 left-0 w-full', className)}>
            <div
                className="relative overflow-visible"
                ref={containerRef}
                style={{ height: stripHeight, width: stripWidth }}
            >
                <DraggableCore
                    nodeRef={selectionRef}
                    onStart={handleDragStart}
                    onDrag={handleDrag}
                    onStop={handleDragStop}
                >
                    <div
                        ref={selectionRef}
                        className={twMerge(
                            'absolute pointer-events-auto selection-handle top-0',
                            // Must mirror the inner shape's corners, or the focus
                            // ring traces a different outline than the selection.
                            'z-10 transition-colors',
                            'rounded-tl-sm rounded-tr-sm rounded-br-md rounded-bl-sm',
                            'outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1 focus-visible:ring-offset-transparent',
                        )}
                        style={{
                            left: `${snap(selectionLeft)}px`,
                            width: `${snap(selectionWidth)}px`,
                            height: `${stripHeight}px`,
                            cursor: 'move',
                        }}
                        onKeyDown={(e) => handleKeyDown(e, selection)}
                        tabIndex={0}
                        role="slider"
                        aria-label={`Video selection indicator. Duration: ${selection.duration} seconds. Start time: ${selection.startTime.toFixed(1)} seconds. Use arrow keys to move, Enter or Space to play/pause, Backspace to reset.`}
                        aria-valuemin={0}
                        aria-valuemax={videoDuration}
                        aria-valuenow={selection.startTime}
                        aria-valuetext={`Selection at ${selection.startTime.toFixed(1)} seconds, duration ${selection.duration.toFixed(1)} seconds`}
                    >
                        <div
                            className={twMerge(
                                // 2px top/bottom + 11px end caps yields the spec's
                                // 278x37 inner area inside a 300x41 selection.
                                'w-full h-full relative border-y-2 border-yellow-400',
                                'rounded-tl-sm rounded-tr-sm rounded-br-md rounded-bl-sm',
                            )}
                            style={{ cursor: 'move' }}
                        >
                            <EndCap side="left" />

                            <DurationLabel duration={selection.duration} selectionWidth={selectionWidth} />

                            <EndCap side="right" />
                        </div>

                        <Tooltip
                            open={leftTooltipVisible}
                            trigger={
                                <TrimHandle
                                    position="left"
                                    onDragStart={handleLeftTrimStart}
                                    onDrag={handleLeftTrimDrag}
                                    onDragStop={handleLeftTrimStop}
                                    onNudge={nudgeLeftTrim}
                                />
                            }
                            title={`${selection.startTime.toFixed(1)}s`}
                            variant="light"
                            hasArrow
                            className="pointer-events-none"
                            contentProps={{ side: 'top', sideOffset: 8 }}
                        />

                        <Tooltip
                            open={rightTooltipVisible}
                            trigger={
                                <TrimHandle
                                    position="right"
                                    onDragStart={handleRightTrimStart}
                                    onDrag={handleRightTrimDrag}
                                    onDragStop={handleRightTrimStop}
                                    onNudge={nudgeRightTrim}
                                />
                            }
                            title={`${selection.endTime.toFixed(1)}s`}
                            variant="light"
                            hasArrow
                            className="pointer-events-none"
                            contentProps={{ side: 'top', sideOffset: 8 }}
                        />
                    </div>
                </DraggableCore>
            </div>
        </div>
    );
}
